import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { DataService } from 'src/core/services/data.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { Table, TableModule } from 'primeng/table';
import { ChartConfiguration, ChartType, Chart, ChartDataset } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ChatService } from 'src/core/services/chat.service';
import { DaterangeService } from 'src/core/services/daterange.service';
import { defer, iif, map, merge, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ModalService } from 'src/core/services/modal.service';
import { LoadingService } from 'src/core/services/loading.service';
import { WidgetService } from 'src/core/services/widget.service';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { NgShuiModule } from '@shui/angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { SortEvent } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { MaintenanceDetails } from 'src/core/types/systemhealth.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from 'src/core/types/general.types';
import {  registerables,TimeScale } from 'chart.js';
import 'chartjs-adapter-date-fns'; // For time scale
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { DeviceService } from 'src/core/services/device.service';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

// Register Chart.js components
Chart.register(...registerables,TimeScale);

@Component({
  selector: 'app-maintenance-details',
  imports: [CommonModule, TableModule, BaseChartDirective, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './maintenance-details.component.html',
  styleUrl: './maintenance-details.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MaintenanceDetailsComponent {
  failure_reasons: any;
  popoverOpen: boolean;

  constructor(private dataService: DataService, private selectedSystemService: SelectedsystemService, private chatService: ChatService, private daterangeService: DaterangeService, private modalService: ModalService, private loadingService: LoadingService, private widgetService: WidgetService, private selectedContentService: SelectedContentService, private deviceService: DeviceService, private translocoService: TranslocoService) { }

  @ViewChild('table') table: Table;
  @ViewChild('popover_failureReasons') failureReasonsPopover: any;
  @ViewChild('tooltip') tooltip: any;

  maintenance: any = null;
  isLoading: boolean = true;
  isError: boolean = false;

  searchTerm: string = '';
  selectedRange: number;

  @Input() tabs: any[] = [];
  selectedWidget: string = "None";
  displayedTabs: any[] = [];
  activeTab: string = "Activities";
  workareaSize: workareaSize = "small";
  screenSize: string = 'Large';
  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
   onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  };
  switchTab = this.widgetService.switchTab.bind(this.widgetService);
  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory') => {
    this.daterangeService.changeRange(range, type);
  };

  public chartConfigs: { [key: string]: ChartConfiguration<'line'> } = {};

  private baseDatasetStyle: Partial<ChartDataset<'line'>> = {
    backgroundColor: 'rgba(251, 191, 40, 0.15)',
    borderColor: '#FBBF28',
    pointBackgroundColor: '#FBBF28',
    pointBorderColor: '#FBBF28',
    pointBorderWidth: 0,
    pointRadius: 2.5,
    pointHoverRadius: 2.5,
    borderWidth: 4,
    tension: 0.35,
    fill: 'origin'
  };

  private destroyRef = inject(DestroyRef);

  failureChartData: any;
  failureChartOptions: any;

  ngOnInit(): void {

    this.loadingService.$isMaintenanceLoading.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    this.loadingService.$isMaintenanceError.subscribe((error) => {
      this.isError = error;
    });

    //Check which tabs to display
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);
      }
    });

    //Check which tab is currently active
    this.widgetService.$activeTab.subscribe((tab) => {
      if (tab) {
        this.activeTab = tab;
      }
      else {
        this.activeTab = this.displayedTabs[0]?.name;
      }
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    this.deviceService.size$.subscribe((size) => {
      this.screenSize = size;
    });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeSystemHealth;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshMaintenance.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isMaintenanceLoading.next(true);
        this.loadingService.$isMaintenanceError.next(false);
        this.maintenance = null;
        this.chartConfigs = {};
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.maintenancePullDate;
        const pulledFor = this.selectedSystemService.maintenancePulledFor;

        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';

        const shouldFetch =
          force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== (serial + material);

        return iif(
          () => shouldFetch,
          this.dataService.getMaintenance(material, serial).pipe(
            tap(({ response, status }: ApiResponse<MaintenanceDetails>) => {
              console.log(force ? 'Forced refresh of maintenance data' : 'Fetching new maintenance data');
              this.selectedSystemService.maintenancePullDate = now;
              this.selectedSystemService.maintenancePulledFor = serial + material;
              if (status === 200) {
                this.selectedSystemService.$maintenance.next(response);
              } else if (status === 204) {
                this.selectedSystemService.$maintenance.next(null);
                this.loadingService.$isMaintenanceEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$maintenance.pipe(
            take(1),
            tap(() => console.log('Using cached maintenance data')),
            map((cached): ApiResponse<MaintenanceDetails> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response, status }: ApiResponse<MaintenanceDetails>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      {
        next: ({ response, rangeValue }: { response: MaintenanceDetails, rangeValue: any }) => {

          //Assign data
          this.selectedRange = rangeValue;

          const data = response?.maintenance_data || [];
          this.maintenance = data;
          //this.failure_reasons = this.maintenance.map(item => item.metrics['' + this.selectedRange].failure_reasons).flat();

          //Generate Charts from Data
          data.forEach(row => {
            const key = row.routine_name;
            const rowData = row.metrics[this.selectedRange]["trend"];
            this.chartConfigs[key] = this.createChart(rowData);

          });

          this.loadingService.$isMaintenanceLoading.next(false);
        },
        error: (error) => {
          console.error("Error fetching maintenance data:", error);
          this.maintenance = [];
          this.loadingService.$isMaintenanceError.next(true);
          this.loadingService.$isMaintenanceLoading.next(false);
        }
      });

  }

  onSearchApply(event: any) {
    this.searchTerm = event.value?.toLowerCase() || '';
  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  askChat(routine: string) {
    this.widgetService.$chatPipSize.next('maximized');
    const prompt = this.translocoService.translate('maintenanceDetails.prompt.fixMaintenance', {
      routineName: routine,
      serialNumber: this.selectedContentService.currentSystem?.SerialNumber || ''
    });

    if (shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value)) {
      this.chatService.addMessage(prompt, true, undefined, true);
      return;
    }

    this.chatService.addMessage(prompt, true);

  }

  hasNonZeroTrend(rowData?: number[] | null): boolean {
    return Array.isArray(rowData) && rowData.some(value => Number(value) !== 0);
  }


  createChart(rowData: number[]): ChartConfiguration<'line'> {
    return {

      type: 'line',
      data: {
        labels: Array(rowData.length).fill(''),
        datasets: [{
          ...this.baseDatasetStyle,
          data: rowData
        }]
      },
      options: {
        devicePixelRatio: 4,
        interaction: {
          mode: null
        },
        responsive: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        }
      }
    };
  }

  openFailureModal(row: any, event: any) {
    // this.failure_reasons = row['metrics']['' + this.selectedRange]['failure_reasons'];
    // this.failureReasonsPopover.nativeElement.target = event.currentTarget;
    // this.popoverOpen = true;
    this.modalService.$modalChanged.next({ modal: "maintenance-failure-reasons", open: true, filter: [{ value: {row: row, selectedRange: this.selectedRange }, type: "any" }] })
  }
  openCharts() {
    this.modalService.$modalChanged.next({ modal: "maintenance", open: true })
  }

  showTooltip(key: string, event) {
    this.tooltip.nativeElement.target = event?.target;
    this.tooltip.nativeElement.innerHTML = this.translocoService.translate(key);
    this.tooltip.nativeElement.visible = true;
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'failed':
        case 'succeeded':
        case 'not_performed':
        case 'not_performed_without_comment':
          val1 = a.metrics?.[this.selectedRange]?.[field] ?? 0;
          val2 = b.metrics?.[this.selectedRange]?.[field] ?? 0;
          break;

        case 'last_failed_date': // assuming this is a date field
          val1 = new Date(a.last_failed_date);
          val2 = new Date(b.last_failed_date);
          break;

        case 'routine_name': // string field
          val1 = a.routine_name?.toLowerCase() ?? '';
          val2 = b.routine_name?.toLowerCase() ?? '';
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

  refresh() {
    this.selectedSystemService.$refreshMaintenance.next();
  }


}
